import { ClipboardCheck, FilePlus2, Handshake, Landmark } from 'lucide-react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { CellUser } from '@/shared/components/data-table'
import {
  type SidePanelAction,
  SidePanelActions
} from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Badge } from '@/shared/ui/badge'
import type { HandoverRow } from '../models/delivery-v2'
import { describeDeliveryPanel } from '../operations/delivery-panel-summary'

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</p>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground">{label}</p>
      <div className="mt-0.5 truncate font-medium">{children}</div>
    </div>
  )
}

/**
 * El resumen de una fila de Postventa (T10.6b): negociación · crédito · cliente.
 *
 * Mismo molde que `credit-detail-panel`: se direcciona por la FILA (`neg_`), que existe tenga o no
 * acta, y todo lo que muestra viaja en ella —no pide nada al servidor—. La acción principal es la
 * que antes hacía el clic en la fila: abrir el acta si existe, iniciarla si no.
 */
export function DeliveryDetailPanel({ row }: { row: HandoverRow }) {
  const navigate = useNavigate()
  const closePanel = useSidePanelStore((state) => state.closePanel)
  const panel = describeDeliveryPanel(row)

  const open = (path: string) => {
    closePanel()
    navigate(path)
  }

  const actions: SidePanelAction[] = [
    {
      label: panel.primaryAction.label,
      icon: row.delivery ? (
        <ClipboardCheck aria-hidden="true" className="size-4" />
      ) : (
        <FilePlus2 aria-hidden="true" className="size-4" />
      ),
      onClick: () => open(panel.primaryAction.path),
      variant: 'default',
      primary: true
    },
    {
      label: 'Abrir negociación',
      icon: <Handshake aria-hidden="true" className="size-4" />,
      onClick: () => open(panel.negotiation.path)
    },
    ...(panel.credit
      ? [
          {
            label: 'Abrir expediente de crédito',
            icon: <Landmark aria-hidden="true" className="size-4" />,
            onClick: () => open((panel.credit as NonNullable<typeof panel.credit>).path)
          }
        ]
      : [])
  ]

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SectionTitle>Entrega</SectionTitle>
              <p className="mt-1 text-sm font-medium leading-5">{panel.handover.act}</p>
            </div>
            <Badge
              variant="secondary"
              className={cn('shrink-0 border-0', panel.handover.statusClassName)}
            >
              {panel.handover.statusLabel}
            </Badge>
          </div>
          {row.delivery && (
            <div className="grid grid-cols-2 gap-2 rounded-md bg-secondary/70 p-2.5 text-xs">
              <Fact label="Programada">{panel.handover.scheduledDate}</Fact>
              <Fact label="Entregada el">{panel.handover.completedAt}</Fact>
            </div>
          )}
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions actions={actions} />
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelSectionBody>
          <SectionTitle>Negociación</SectionTitle>
          <div className="grid grid-cols-2 gap-2 rounded-md bg-secondary/70 p-2.5 text-xs">
            <Fact label="Código">{panel.negotiation.code}</Fact>
            <Fact label="Cerrada">{panel.negotiation.closedAt}</Fact>
            <Fact label="Inmueble">
              {panel.negotiation.property}
              {panel.negotiation.propertyCode && (
                <span className="block truncate font-normal text-muted-foreground">
                  {panel.negotiation.propertyCode}
                </span>
              )}
            </Fact>
            <Fact label="Proyecto">
              <ProjectBadge
                name={panel.negotiation.projectName ?? undefined}
                code={panel.negotiation.projectCode ?? undefined}
              />
            </Fact>
            <div className="col-span-2 min-w-0">
              <p className="text-muted-foreground">Asesor</p>
              <CellUser
                className="mt-1"
                name={panel.negotiation.advisorName ?? undefined}
                avatar={panel.negotiation.advisorAvatar ?? undefined}
                emptyLabel="Sin asesor"
              />
            </div>
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelSectionBody>
          <div className="flex items-start justify-between gap-3">
            <SectionTitle>Crédito</SectionTitle>
            {panel.credit && (
              <Badge
                variant="secondary"
                className={cn('shrink-0 border-0', panel.credit.statusClassName)}
              >
                {panel.credit.statusLabel}
              </Badge>
            )}
          </div>
          {panel.credit ? (
            <div className="grid grid-cols-2 gap-2 rounded-md bg-secondary/70 p-2.5 text-xs">
              <Fact label="Expediente">Crédito #{panel.credit.code}</Fact>
              <Fact label="Aprobado el">{panel.credit.approvedAt}</Fact>
              <div className="col-span-2 min-w-0">
                <p className="text-muted-foreground">Dónde va</p>
                <p className="mt-0.5 font-medium">{panel.credit.position}</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Esta negociación no tiene expediente de crédito. Entró a Postventa porque ya tiene
              acta.
            </p>
          )}
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection separated={false}>
        <SidePanelSectionBody>
          <SectionTitle>{panel.clients.length > 1 ? 'Clientes' : 'Cliente'}</SectionTitle>
          {panel.clients.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {panel.clients.map((client) => (
                <li key={client.id} className="min-w-0">
                  <CellUser name={client.name} avatar={client.photo ?? undefined} />
                  {client.caption && (
                    <p className="mt-0.5 pl-9 text-xs text-muted-foreground">{client.caption}</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">Propietarios sin resolver</p>
          )}
        </SidePanelSectionBody>
      </SidePanelSection>
    </div>
  )
}

export const deliveryDetailPanelIcon = <ClipboardCheck aria-hidden="true" />
